import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Link, Typography } from '@mui/material';
import { useCallback } from 'react';
import type { StructuredContentDocument } from '@repo/supergraph-schemas/structuredContent/types';
import { Details } from '../../../../Details';
import { NerdWalletBlackIcon } from '../../../../Icons/NerdWalletBlackIcon';
import { StructuredContentRenderer } from '../../../../StructuredContentRenderer';
import { ProsAndConsSection } from '../components/ProsAndConsSection';
import { useProductCardEvents } from './hooks';

export const NerdwalletTake = ({
  content,
  reviewLink,
  pros,
  cons,
  id,
}: {
  content?: StructuredContentDocument;
  reviewLink?: string;
  pros?: StructuredContentDocument[] | string[];
  cons?: StructuredContentDocument[] | string[];
  id: string;
}) => {
  const { handleReadFullReviewClick, handleAccordionInteraction } =
    useProductCardEvents(reviewLink);

  const handleSummaryClick = useCallback(
    (event: React.MouseEvent) => {
      const detailsEl = (event.currentTarget as HTMLElement).closest('details');
      if (detailsEl) {
        handleAccordionInteraction(detailsEl.open ? 'collapse' : 'expand');
      }
    },
    [handleAccordionInteraction],
  );

  return (
    <Details
      variant="simple"
      summaryComponentProps={{
        sx: {
          boxShadow: 'none',
          border: '0px',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          justifyContent: 'space-between',
          paddingX: 2,
          paddingY: '12px',
          '&:hover': {
            outline: '2px solid',
            outlineColor: 'blues.light',
          },
        },
        id: `nerdwallet-take-header-${id}`,
        'aria-controls': `nerdwallet-take-details-${id}`,
        onClick: handleSummaryClick,
      }}
      SummaryComponent={
        <>
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <NerdWalletBlackIcon />
            <Typography component="span" sx={{ fontWeight: 500 }}>
              NerdWallet&apos;s take
            </Typography>
          </Box>
          <ExpandMoreIcon className="expand-icon" />
        </>
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          padding: 2,
          paddingBottom: 4,
        }}
        id={`nerdwallet-take-details-${id}`}
      >
        <Box sx={{ backgroundColor: 'greens.extraLight', p: 3, borderRadius: '0.25rem' }}>
          <StructuredContentRenderer doc={content} />
          {reviewLink && (
            <Link
              onClick={handleReadFullReviewClick}
              href={reviewLink}
              sx={{ color: 'greens.dark', fontWeight: 500, py: 2 }}
            >
              Read full review
            </Link>
          )}
        </Box>
        <ProsAndConsSection pros={pros} cons={cons} />
      </Box>
    </Details>
  );
};
